import { FC, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import {
	useCreateGroupLabel,
	useUpdateGroupLabel,
	useGroupLabels,
	useDeleteGroupLabel,
} from "../../hooks/groupLabels";
import { useUserStore } from "../../stores/user.store";
import {
	GroupLabel,
	GroupLabelUpdateRequest,
} from "@/domain/groupLabel.interface";

const OrganizationLevelsComponent: FC = () => {
	// Fetch group labels
	const { data: groupLabels, isLoading, error } = useGroupLabels();
	const { userInfo } = useUserStore();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingLevel, setEditingLevel] = useState<GroupLabel | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		status: "ACTIVE",
		createdBy: userInfo?.id || "SYSTEM_USER",
	});
	const createMutation = useCreateGroupLabel();
	const updateMutation = useUpdateGroupLabel();
	const deleteMutation = useDeleteGroupLabel();
	// Edit handler
	const handleEditLabel = (label: GroupLabel) => {
		setEditingLevel(label);
		setFormData({
			name: label.name,
			description: label.description || "",
			status: "ACTIVE",
			createdBy: label.createdBy,
		});
		setShowCreateForm(true);
	};

	// Delete handler
	const handleDeleteLabel = async (id: string) => {
		if (window.confirm("Are you sure you want to delete this group label?")) {
			try {
				await deleteMutation.mutateAsync(id);
			} catch (err) {
				// Error handled by React Query
			}
		}
	};

	const handleCreateLevel = async () => {
		try {
			await createMutation.mutateAsync(formData);
			handleCloseForm();
		} catch (err) {
			// Error will be handled by React Query
		}
	};

	const handleCloseForm = () => {
		setShowCreateForm(false);
		setEditingLevel(null);
		setFormData({
			name: "",
			description: "",
			status: "ACTIVE",
			createdBy: userInfo?.id || "SYSTEM_USER",
		});
	};
	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setPageSize(parseInt(event.target.value, 10));
		setPage(0);
	};
	//const totalCount = allLevels.length;
	const handleUpdateLevel = async () => {
		if (!editingLevel) return;

		try {
			const updateData: GroupLabelUpdateRequest = {
				...formData,
				_id: editingLevel._id,
				updatedBy: userInfo?.id || "SYSTEM_USER",
			};
			await updateMutation.mutateAsync(updateData);
			handleCloseForm();
		} catch (err) {
			// Error will be handled by React Query
		}
	};
	/*
	
	

	

	const handleDeleteLevel = async (levelId: string) => {
		if (
			window.confirm("Are you sure you want to delete this organization level?")
		) {
			try {
				await deleteMutation.mutateAsync(levelId);
			} catch (err) {
				// Error will be handled by React Query
			}
		}
	};

	const handleEditLevel = (level: IOrganizationLevel) => {
		setEditingLevel(level);
		setFormData({
			name: level.name,
			level: level.level,
			createdBy: userInfo?.id || "SYSTEM_USER",
		});
		setShowCreateForm(true);
	};

	

	

	if (isLoading) return <Typography>Loading Group Labels...</Typography>;
	if (error)
		return (
			<Typography color="error">Error loading group labels</Typography>
		);
*/
	// Filter and paginate
	const filteredLabels = (groupLabels || []).filter(
		(label) =>
			label.name.toLowerCase().includes(search.toLowerCase()) ||
			label.description?.toLowerCase().includes(search.toLowerCase()),
	);
	const paginatedLabels = filteredLabels.slice(
		page * pageSize,
		(page + 1) * pageSize,
	);

	return (
		<Box sx={{ p: 3 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4" component="h1">
					Group Labels Management
				</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<AddIcon />}
					onClick={() => setShowCreateForm(true)}
				>
					Add Group Label
				</Button>
			</Box>

			{/* Search */}
			<Box mb={3}>
				<TextField
					fullWidth
					label="Search organization levels"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<SearchIcon sx={{ mr: 1, color: "action.active" }} />
						),
					}}
				/>
			</Box>

			{/* Create/Edit Form */}
			{showCreateForm && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{editingLevel ? "Edit Group Label" : "Create New Group Label"}
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={8}>
								<TextField
									fullWidth
									label="Name"
									value={formData.name}
									onChange={(e) =>
										setFormData({ ...formData, name: e.target.value })
									}
									required
								/>
								<TextField
									fullWidth
									label="Description"
									value={formData.description}
									onChange={(e) =>
										setFormData({ ...formData, description: e.target.value })
									}
									required
								/>
							</Grid>
						</Grid>
						<Box mt={2} display="flex" gap={2}>
							<Button
								variant="contained"
								color="primary"
								onClick={editingLevel ? handleUpdateLevel : handleCreateLevel}
								disabled={createMutation.isPending || updateMutation.isPending}
							>
								{editingLevel ? "Update Group Label" : "Create Group Label"}
							</Button>
							<Button variant="outlined" onClick={handleCloseForm}>
								Cancel
							</Button>
						</Box>
					</CardContent>
				</Card>
			)}

			{/* Group Labels Table */}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Name</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Created By</TableCell>
								<TableCell>Created At</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={6} align="center">
										Loading group labels...
									</TableCell>
								</TableRow>
							) : error ? (
								<TableRow>
									<TableCell
										colSpan={6}
										align="center"
										style={{ color: "red" }}
									>
										Error loading group labels
									</TableCell>
								</TableRow>
							) : paginatedLabels.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center">
										No group labels found
									</TableCell>
								</TableRow>
							) : (
								paginatedLabels.map((label) => (
									<TableRow key={label._id}>
										<TableCell>{label.name}</TableCell>
										<TableCell>{label.description}</TableCell>
										<TableCell>{label.createdBy}</TableCell>
										<TableCell>
											{label.createdAt
												? new Date(label.createdAt).toLocaleString()
												: ""}
										</TableCell>
										<TableCell align="right">
											<Button
												size="small"
												variant="outlined"
												color="primary"
												onClick={() => handleEditLabel(label)}
												style={{ marginRight: 8 }}
											>
												Edit
											</Button>
											<Button
												size="small"
												variant="outlined"
												color="error"
												onClick={() => handleDeleteLabel(label._id)}
												disabled={deleteMutation.isPending}
											>
												Delete
											</Button>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={filteredLabels.length}
						rowsPerPage={pageSize}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</TableContainer>
			</Paper>
		</Box>
	);
};

export default OrganizationLevelsComponent;
